import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField, tagMultiSelectToString } from '@repo/shared-client/components/fields'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import ProfileCard from '@web/src/features/profile/profile-card/profile-card'
import {
  type ProfileListFilterFormValues,
  profileListFilterFormValuesFromResolvedParams,
} from '@web/src/features/profile/profile-list-filter-types'
import ProfileListSideNav from '@web/src/features/profile/profile-list-side-nav/profile-list-side-nav'
import ProfileListTopNav from '@web/src/features/profile/profile-list-top-nav/profile-list-top-nav'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { ProfileInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { RelationshipEnum } from '@web/src/lib/types/enumTypes'
import { UsersIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  ProfileSortEnum,
  ProfilesQuery,
  ProfilesQueryVariables,
  RelationshipEnum as RelationshipFilterEnum,
} from 'types/graphql'
import { z } from 'zod'

const PROFILES_QUERY: TypedDocumentNode<ProfilesQuery, ProfilesQueryVariables> = gql`
  query ProfilesQuery($page: Int, $filter: ProfileFilter, $sortBy: ProfileSortEnum) {
    profilesPage(page: $page, filter: $filter, sortBy: $sortBy) {
      profiles {
        ...ProfileInfo
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

type ProfileListSearchInput = {
  page?: number
  name?: string
  minAge?: number
  maxAge?: number
  distance?: number
  relationship?: string
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  hasMedia?: string
  isVerified?: string
}

function resolveProfileListSearch(
  search: ProfileListSearchInput,
  profileFallback?: { location?: string; locationLat?: number; locationLong?: number }
) {
  return {
    page: search.page ?? 1,
    name: search.name,
    minAge: search.minAge ?? 18,
    maxAge: search.maxAge ?? 100,
    distance: search.distance ?? 1000,
    relationship: search.relationship,
    location: search.location ?? profileFallback?.location,
    locationLat: search.locationLat ?? profileFallback?.locationLat,
    locationLong: search.locationLong ?? profileFallback?.locationLong,
    sortBy: search.sortBy ?? 'DISTANCE',
    hasMedia: search.hasMedia === 'true',
    isVerified: search.isVerified === 'true',
  }
}

const isRelationshipFilterEnum = (value: string): value is RelationshipFilterEnum => {
  return value in RelationshipEnum && Number.isNaN(Number(value))
}

export const Route = createFileRoute('/(auth)/(main-layout)/profile/list')({
  component: ProfileSearchPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    minAge: z.coerce.number().optional(),
    maxAge: z.coerce.number().optional(),
    distance: z.coerce.number().optional(),
    relationship: z.string().optional(),
    location: z.string().optional(),
    locationLat: z.coerce.number().optional(),
    locationLong: z.coerce.number().optional(),
    sortBy: z.string().optional(),
    hasMedia: z.string().optional(),
    isVerified: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        name: '',
        minAge: 18,
        maxAge: 100,
        distance: 1000,
        relationship: '',
        location: '',
        locationLat: 0,
        locationLong: 0,
        sortBy: 'DISTANCE',
      }),
    ],
  },
  loaderDeps: ({
    search: {
      page,
      name,
      minAge,
      maxAge,
      distance,
      relationship,
      location,
      locationLat,
      locationLong,
      sortBy,
      hasMedia,
      isVerified,
    },
  }) => ({
    page,
    name,
    minAge,
    maxAge,
    distance,
    relationship,
    location,
    locationLat,
    locationLong,
    sortBy,
    hasMedia,
    isVerified,
  }),
  loader: async ({ context, deps }) => {
    const relationshipFilters = deps.relationship ? deps.relationship.split(',').filter(isRelationshipFilterEnum) : []

    const page = deps.page ? Number.parseInt(String(deps.page), 10) : 1

    const variables: ProfilesQueryVariables = {
      page,
      filter: {
        page,
        name: deps.name ? String(deps.name) : '',
        minAge: deps.minAge ? Number.parseInt(String(deps.minAge), 10) : 18,
        maxAge: deps.maxAge ? Number.parseInt(String(deps.maxAge), 10) : 100,
        distance: deps.distance ? Number.parseInt(String(deps.distance), 10) : 1000,
        relationship: relationshipFilters,
        location: deps.location ? String(deps.location) : undefined,
        locationLat: deps.locationLat ? Number.parseFloat(String(deps.locationLat)) : undefined,
        locationLong: deps.locationLong ? Number.parseFloat(String(deps.locationLong)) : undefined,
        hasMedia: deps.hasMedia === 'true',
        isVerified: deps.isVerified === 'true',
      },
      sortBy: (deps.sortBy ? String(deps.sortBy) : 'DISTANCE') as ProfileSortEnum,
    }

    const { queryRef } = await queryAndPreload(context, PROFILES_QUERY, { variables })
    return { queryRef }
  },
  head: () =>
    seo({
      title: `Profile Search | ${env.VITE_APP_NAME}`,
      description: 'Profiles search page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function ProfileSearchPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const search = useSearch({ strict: false })
  const { currentUser, hasPermission, isAuthenticated } = useAuth()
  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const profileFallback = useMemo(
    () =>
      currentUser?.profile
        ? {
            location: currentUser.profile.location ?? undefined,
            locationLat: currentUser.profile.locationLat ?? undefined,
            locationLong: currentUser.profile.locationLong ?? undefined,
          }
        : undefined,
    [currentUser?.profile]
  )

  const resolvedSearch = useMemo(() => resolveProfileListSearch(search, profileFallback), [search, profileFallback])

  const listFilterDefaults = useMemo(
    () =>
      profileListFilterFormValuesFromResolvedParams({
        name: resolvedSearch.name,
        minAge: resolvedSearch.minAge,
        maxAge: resolvedSearch.maxAge,
        distance: resolvedSearch.distance,
        relationship: resolvedSearch.relationship ?? null,
        location: resolvedSearch.location,
        locationLat: resolvedSearch.locationLat,
        locationLong: resolvedSearch.locationLong,
        sortBy: resolvedSearch.sortBy,
        hasMedia: resolvedSearch.hasMedia,
        isVerified: resolvedSearch.isVerified,
      }),
    [resolvedSearch]
  )

  const listFilterResetValues = useMemo(
    () =>
      profileListFilterFormValuesFromResolvedParams({
        name: '',
        minAge: 18,
        maxAge: 100,
        distance: 1000,
        relationship: null,
        location: profileFallback?.location,
        locationLat: profileFallback?.locationLat,
        locationLong: profileFallback?.locationLong,
        sortBy: 'DISTANCE',
        hasMedia: false,
        isVerified: false,
      }),
    [profileFallback?.location, profileFallback?.locationLat, profileFallback?.locationLong]
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: search.sortBy ?? 'DISTANCE' },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: search.sortBy ?? 'DISTANCE' })
  }, [search.sortBy, sortFormMethods])

  const applyListFilters = useCallback(
    (values: ProfileListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name?.trim() || undefined,
          relationship: tagMultiSelectToString(values.relationship ?? []) || undefined,
          minAge: values.age[0] !== 18 ? values.age[0] : undefined,
          maxAge: values.age[1] !== 100 ? values.age[1] : undefined,
          distance: values.distance[0] < 1000 ? values.distance[0] : undefined,
          hasMedia: values.hasMedia ? 'true' : undefined,
          isVerified: values.isVerified ? 'true' : undefined,
          location: values.location?.name || undefined,
          locationLat: values.location?.latitude || undefined,
          locationLong: values.location?.longitude || undefined,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      navigate({ search: { ...searchRef.current, page: nextPage }, replace: true })
    },
    [navigate]
  )

  const handleReset = useCallback(() => {
    navigate({ to: '/profile/list', replace: true } as Parameters<typeof navigate>[0])
  }, [navigate])

  const { data, error } = useReadQuery(loaderData.queryRef as QueryRef<ProfilesQuery>)
  const profilesPage = data?.profilesPage ?? null

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <div className="flex w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <ProfileListSideNav
          defaultLocation={search.location ?? currentUser?.profile?.location ?? undefined}
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          hasPermission={hasPermission}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
        />
      </aside>

      <div className="flex w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="w-full max-w-full px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Profile Search') }]}
            className="mb-0 items-stretch pb-4"
            highlightTitle={t('Profiles')}
            preTitle={t('Discover')}
            rightChild={
              <div className="hidden h-full flex-col items-end gap-3 md:flex">
                <div className="mt-auto">
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 text-nowrap font-medium text-muted-foreground text-sm">
                      {t('Sort By:')}
                    </span>
                    <Form formMethods={sortFormMethods} suppressHydrationWarning>
                      <NativeSelectFormField name="sortBy" onValueChange={handleSortChange}>
                        <option value="DISTANCE">{t('Distance')}</option>
                        <option value="LATEST">{t('Latest')}</option>
                        <option value="ONLINE">{t('Online')}</option>
                      </NativeSelectFormField>
                    </Form>
                  </div>
                </div>
              </div>
            }
            subtitle={t('Find members near you or filter by age, relationship, and more.')}
          />

          <div className="mb-6 w-full md:hidden">
            <ProfileListTopNav
              defaultLocation={search.location ?? currentUser?.profile?.location ?? undefined}
              filterDefaults={listFilterDefaults}
              filterResetValues={listFilterResetValues}
              hasPermission={hasPermission}
              isAuthenticated={isAuthenticated}
              onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
              onReset={handleReset}
            />
          </div>

          {profilesPage && profilesPage.profiles.length > 0 ? (
            <div className="flex w-full flex-col gap-0">
              <div className="flex w-full flex-wrap content-start items-stretch justify-evenly gap-3">
                {profilesPage.profiles.map((profile) => (
                  <div
                    className="box-border flex h-full min-w-0 max-w-full flex-[1_1_calc((100%-1rem)/2)] flex-col md:w-[200px] md:flex-none"
                    key={profile.id}
                  >
                    <ProfileCard className="w-full" profile={profile} />
                  </div>
                ))}
              </div>

              <PaginationControl
                count={profilesPage.count ? profilesPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={resolvedSearch.page}
                pageSize={profilesPage.perPage ? profilesPage.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <UsersIcon className="size-8" />
                </EmptyMedia>
                <EmptyTitle>{t('No profiles found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>
        <Footer />
      </div>
    </div>
  )
}
